import React, { useEffect, useState } from "react";
import { Server, HardDrive, Database, Network, Cloud, Lock, Activity, Zap, ShieldCheck } from "lucide-react";

const LEGACY = [
  { icon: Server, name: "AS/400 Mainframe", detail: "COBOL · est. 2001" },
  { icon: Database, name: "Oracle 11g Cluster", detail: "On-prem · v-motion" },
  { icon: HardDrive, name: "Fibre-Channel SAN", detail: "NetApp 8040" },
  { icon: Network, name: "MPLS backbone", detail: "3 tier · co-lo" },
];

const CLOUD = [
  { icon: Cloud, name: "GovCloud VPC", detail: "us-gov-east-1 / 1b" },
  { icon: Database, name: "RDS PostgreSQL 16", detail: "Multi-AZ · encrypted" },
  { icon: Lock, name: "KMS + HSM", detail: "FIPS 140-3 · CMKs" },
  { icon: Activity, name: "CloudWatch + Splunk", detail: "1s granularity" },
];

function MetricCard({ label, value, unit, delta, positive }) {
  return (
    <div className="panel-solid p-4">
      <div className="eyebrow-muted mb-3">{label}</div>
      <div className="flex items-baseline gap-1">
        <span className="font-mono text-2xl text-slate-100">{value}</span>
        {unit && <span className="font-mono text-[11px] text-slate-500">{unit}</span>}
      </div>
      <div className={`font-mono text-[10px] mt-1 ${positive ? "text-emerald-400" : "text-amber-400"}`}>{delta}</div>
    </div>
  );
}

export default function MigrationDemo({ SectionHeader }) {
  const [progress, setProgress] = useState(0);
  const [migrated, setMigrated] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setProgress((p) => (p >= 100 ? 0 : p + 1));
    }, 60);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      setMigrated((m) => (m >= 1904 ? 0 : m + Math.max(1, Math.floor((1904 - m) / 40))));
    }, 90);
    return () => clearInterval(id);
  }, []);

  return (
    <section id="migration" className="relative py-24 md:py-32 px-6 md:px-10 border-t border-slate-800/50">
      <div className="max-w-[1400px] mx-auto">
        <SectionHeader
          index="04"
          kicker="Live Migration Ops"
          title="Watch a wave move in real time."
          description="A generalized illustration of a Wave 03 execution. Every asset is inventoried, hashed at rest, moved through an encrypted transit lane, and re-validated against the target control baseline before traffic is shifted."
        />

        <div className="panel corner-brackets p-6 md:p-10 reveal">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-3">
              <span className="pill"><span className="dot bg-emerald-400" />Wave 03 · Active</span>
              <span className="font-mono text-[10px] text-slate-500 tracking-widest uppercase">Region us-gov-east-1</span>
            </div>
            <div className="font-mono text-[10px] text-slate-500 tracking-widest uppercase">
              Session #4211 · Operator: cleared
            </div>
          </div>

          <div className="grid md:grid-cols-12 gap-6 items-stretch">
            {/* Legacy */}
            <div className="md:col-span-4 panel-solid p-5">
              <div className="flex items-center justify-between mb-5">
                <div className="eyebrow-muted">Legacy Estate</div>
                <span className="font-mono text-[10px] text-amber-400/90">SUNSETTING</span>
              </div>
              <div className="space-y-3">
                {LEGACY.map((l) => {
                  const Icon = l.icon;
                  return (
                    <div key={l.name} className="flex items-center gap-3 p-3 border border-slate-800 bg-slate-950/60">
                      <Icon size={16} strokeWidth={1.5} className="text-slate-500 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm text-slate-200 truncate">{l.name}</div>
                        <div className="font-mono text-[10px] text-slate-500 truncate">{l.detail}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Flow */}
            <div className="md:col-span-4 flex flex-col items-center justify-center py-4">
              <svg viewBox="0 0 200 240" className="w-full max-w-[220px]" fill="none">
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#38bdf8" stopOpacity="0" />
                    <stop offset="50%" stopColor="#38bdf8" stopOpacity="0.6" />
                    <stop offset="100%" stopColor="#38bdf8" stopOpacity="0" />
                  </linearGradient>
                </defs>
                {[30, 90, 150, 210].map((y, i) => (
                  <g key={y}>
                    <path d={`M 10 ${y} C 70 ${y}, 130 120, 190 120`} stroke="#1e293b" strokeWidth="1" />
                    <path d={`M 10 ${y} C 70 ${y}, 130 120, 190 120`} stroke="#38bdf8" strokeWidth="1" className="flow-line" style={{ animationDelay: `${i * 0.4}s` }} />
                  </g>
                ))}
                <circle cx="190" cy="120" r="6" fill="#38bdf8" fillOpacity="0.15" stroke="#38bdf8" />
              </svg>
              <div className="mt-4 font-mono text-[10px] tracking-[0.25em] text-slate-500 uppercase text-center">Encrypted Transit · AES-256-GCM</div>
              <div className="mt-6 w-full max-w-[240px]">
                <div className="flex justify-between font-mono text-[10px] text-slate-500 uppercase tracking-widest mb-2">
                  <span>Wave 03</span>
                  <span>{progress}%</span>
                </div>
                <div className="h-1 bg-slate-800 relative overflow-hidden progress-bar">
                  <div className="h-full bg-cyan-400/60" style={{ width: `${progress}%` }} />
                </div>
              </div>
            </div>

            {/* Target */}
            <div className="md:col-span-4 panel-solid p-5">
              <div className="flex items-center justify-between mb-5">
                <div className="eyebrow-muted">Target Cloud</div>
                <span className="font-mono text-[10px] text-emerald-400">OPERATIONAL</span>
              </div>
              <div className="space-y-3">
                {CLOUD.map((c) => {
                  const Icon = c.icon;
                  return (
                    <div key={c.name} className="flex items-center gap-3 p-3 border border-cyan-400/20 bg-cyan-400/5">
                      <Icon size={16} strokeWidth={1.5} className="text-cyan-300 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm text-slate-100 truncate">{c.name}</div>
                        <div className="font-mono text-[10px] text-slate-500 truncate">{c.detail}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard label="Assets migrated" value={migrated.toLocaleString()} unit="/ 1,904" delta="+ live stream" positive />
            <MetricCard label="Data throughput" value="42.1" unit="Gb/s" delta="peak 58.2" positive />
            <MetricCard label="Integrity failures" value="0" unit="since 04:00" delta="nominal" positive />
            <MetricCard label="Rollback armed" value="YES" delta="snapshot t-0:00:12" positive />
          </div>

          <div className="mt-6 border-t border-slate-800 pt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-2 text-slate-400 text-xs">
              <ShieldCheck size={14} strokeWidth={1.5} className="text-cyan-300" />
              Continuous control assessment against NIST SP 800-53 Rev.5 moderate baseline.
            </div>
            <div className="font-mono text-[10px] tracking-[0.25em] text-slate-500 uppercase">
              Illustrative view · sanitized data · not client identifiable
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
